// One as-built record, printable: who did each step and when, the parts
// actually used, readings, holds and problems raised (T9).
import { useParams } from 'react-router';

import type { AsBuiltRecordResponse } from '../../api/generated/AsBuiltRecordResponse';
import { useRecord } from '../../api/queries';
import { Badge, Button, ErrorPanel, Loading, PageHeader } from '../../design/components';
import { formatDateTime, formatQuantity, titleOf } from '../../design/format';

export function RecordPage() {
    const { buildId = '' } = useParams();
    const record = useRecord(buildId);
    if (record.isPending) return <Loading what="the record" />;
    if (record.isError) return <ErrorPanel error={record.error} title="The record couldn’t be loaded." />;
    return <RecordView record={record.data} />;
}

function RecordView({ record }: { record: AsBuiltRecordResponse }) {
    const { build } = record;
    const stepTitle = (number: number) =>
        build.steps.find((step) => step.number === number)?.title ?? `Step ${String(number)}`;
    return (
        <article className="sf-stack" aria-labelledby="record-heading">
            <div className="sf-row sf-no-print">
                <Button
                    onClick={() => {
                        window.print();
                    }}
                >
                    Print
                </Button>
                <a
                    className="sf-button sf-button--outlined"
                    href={`/api/v1/as-built/${build.summary.buildId}/csv`}
                    download
                >
                    Download CSV
                </a>
            </div>
            <PageHeader
                id="record-heading"
                eyebrow="As-built record"
                title={titleOf(build.summary.assemblyPartNumber, build.summary.assemblyName)}
                description={
                    <>
                        {build.summary.buildQuantity} built at {build.summary.station}. Started{' '}
                        {formatDateTime(build.started?.at)} by {build.started?.displayName ?? '—'}; signed off{' '}
                        {formatDateTime(build.signedOff?.at)} by {build.signedOff?.displayName ?? '—'}. Technicians:{' '}
                        {record.technicians.join(', ')}.
                    </>
                }
            />
            <section className="sf-card">
                <h2>Parts used</h2>
                <table className="sf-table">
                    <thead>
                        <tr>
                            <th scope="col">Part</th>
                            <th scope="col">Quantity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {record.partsUsed.map((part) => (
                            <tr key={`${part.activePartNumber}-${part.unitOfMeasure ?? ''}`}>
                                <td>
                                    <span className="sf-mono">{part.activePartNumber}</span> {part.name ?? ''}{' '}
                                    {part.isSubstituted ? <Badge tone="pending">for {part.partNumber}</Badge> : null}
                                </td>
                                <td>{formatQuantity(part.quantity, part.unitOfMeasure)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
            {build.units.map((unit) => (
                <section key={unit.number} className="sf-card">
                    <h2>{build.units.length > 1 ? `Unit ${String(unit.number)}` : 'Steps'}</h2>
                    <table className="sf-table">
                        <thead>
                            <tr>
                                <th scope="col">Step</th>
                                <th scope="col">Done by</th>
                                <th scope="col">Readings</th>
                            </tr>
                        </thead>
                        <tbody>
                            {unit.completions.map((completion) => (
                                <tr key={completion.step}>
                                    <td>
                                        {completion.step}. {stepTitle(completion.step)}
                                    </td>
                                    <td>
                                        {completion.done.displayName}
                                        <div className="sf-muted">{formatDateTime(completion.done.at)}</div>
                                    </td>
                                    <td>
                                        {unit.readings
                                            .filter((reading) => reading.step === completion.step)
                                            .map((reading) => (
                                                <div key={reading.checkId}>
                                                    {reading.checkName}: {formatQuantity(reading.value, reading.unit)}{' '}
                                                    <Badge tone={reading.withinLimits ? 'success' : 'danger'}>
                                                        {reading.withinLimits ? 'in limits' : 'out of limits'}
                                                    </Badge>
                                                    {reading.note ? (
                                                        <span className="sf-muted"> “{reading.note}”</span>
                                                    ) : null}
                                                </div>
                                            ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {unit.signedOff ? (
                        <p className="sf-muted">
                            Signed off by {unit.signedOff.displayName}, {formatDateTime(unit.signedOff.at)}
                        </p>
                    ) : null}
                </section>
            ))}
            {build.holds.length > 0 || build.problems.length > 0 ? (
                <section className="sf-card">
                    <h2>Holds and problems</h2>
                    <ul>
                        {build.holds.map((hold) => (
                            <li key={`hold-${String(hold.number)}`}>
                                Hold {hold.number}: {hold.reason}, {formatDateTime(hold.placed.at)} by{' '}
                                {hold.placed.displayName}
                                {hold.released ? `, resumed ${formatDateTime(hold.released.at)}` : ''}
                                {hold.note ? ` (“${hold.note}”)` : ''}
                            </li>
                        ))}
                        {build.problems.map((problem) => (
                            <li key={problem.buildIssueId}>
                                Problem on {problem.partNumber}
                                {problem.step === null ? '' : ` at step ${String(problem.step)}`}: {problem.note} (
                                {problem.raised.displayName}, {formatDateTime(problem.raised.at)}, {problem.status})
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
            <section className="sf-card">
                <h2>Kit check</h2>
                <ul>
                    {build.kit.lines.map((line) => (
                        <li key={line.number}>
                            <span className="sf-mono">{line.partNumber}</span>{' '}
                            {formatQuantity(line.quantity, line.unitOfMeasure)}: {line.outcome}
                            {line.note ? ` (“${line.note}”)` : ''}
                        </li>
                    ))}
                </ul>
            </section>
        </article>
    );
}
